import {
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
} from "@material-ui/core";
import { DateTimePicker } from "@material-ui/pickers";
import { Moment } from "moment";

import React, { useEffect, useState } from "react";
import { useStressEntryContext } from "./StressEntryContext";
import { stat } from "fs";
import { STRESSOR_CHECK_IN_MODE, STRESSOR_CHECK_OUT_MODE } from "./constants";
import { useUnitVersion } from "./stressEntryQueries";

export const UVIDFields = ({ UnitVersion }: any) => {
  const { state, dispatch } = useStressEntryContext();
  const [formData, setFormData] = useState({
    UVAB_irradiance: "",
    UVAB_total_dose: "",
    UVB_irradiance_ratio: UnitVersion === "v12" ? "4.5" : "",
    Type_of_light_source: UnitVersion === "v12" ? "UV Fluorescent bulbs" : "",
    DUT_operating_condition:
      UnitVersion === "v12" ? "MPP Maximum Power Point" : "SC Short-circuit",
    DUT_exposed_surface: null,
    Set_temperature: UnitVersion === "v12" ? "60" : "",
    Temperature_control_mode: "",
    Calibration_date: null as Moment | null,
    DUT_average_temperature: UnitVersion === "v12" ? "60" : "",
  });

  const lightSourceOptions = [
    "UV Fluorescent bulbs",
    "Metal Halide bulbs",
    "Xenon bulbs",
  ];
  const dutOperatingModes = [
    "SC Short-circuit",
    "OC Open-circuit",
    "MPP Maximum Power Point",
  ];
  const dutExposedSurfaceOptions = [
    "Module front (sun-facing)",
    "Module rear (ground facing)",
  ];
  const temperatureControlOptions = [
    "Module temperature",
    "Black Panel temperature BPT",
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date: Moment | null) => {
    setFormData((prev) => ({ ...prev, Calibration_date: date }));
  };

  useEffect(() => {
    if (UnitVersion === "v12") {
      setFormData((prev) => ({
        ...prev,
        UVB_irradiance_ratio: prev.UVB_irradiance_ratio || "4.5",
        Type_of_light_source:
          prev.Type_of_light_source || "UV Fluorescent bulbs",
        Set_temperature: prev.Set_temperature || "60",
        DUT_average_temperature: prev.DUT_average_temperature || "60",
        DUT_operating_condition: "MPP Maximum Power Point",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        UVB_irradiance_ratio: "",
        Type_of_light_source: "",
        Set_temperature: "",
        DUT_average_temperature: "",
        DUT_operating_condition: "SC Short-circuit",
      }));
    }
  }, [UnitVersion]);

  useEffect(() => {
    const payload = {
      Calibration_date: formData.Calibration_date
        ? formData.Calibration_date.format("YYYY-MM-DD")
        : null,
      UVAB_irradiance: formData.UVAB_irradiance
        ? parseFloat(formData.UVAB_irradiance)
        : null,
      UVAB_total_dose: formData.UVAB_total_dose
        ? parseFloat(formData.UVAB_total_dose)
        : null,
      UVB_irradiance_ratio: formData.UVB_irradiance_ratio
        ? parseFloat(formData.UVB_irradiance_ratio)
        : null,
      Type_of_light_source: formData.Type_of_light_source,
      DUT_operating_condition: formData.DUT_operating_condition
        ? formData.DUT_operating_condition
        : UnitVersion === "v12"
          ? "MPP Maximum Power Point"
          : "SC Short-circuit",
      // DUT_operating_condition: formData.DUT_operating_condition ? formData.DUT_operating_condition : null,
      // DUT_exposed_surface: formData.DUT_exposed_surface,
      DUT_exposed_surface: formData.DUT_exposed_surface
        ? formData.DUT_exposed_surface
        : null,

      Set_temperature: formData.Set_temperature
        ? parseFloat(formData.Set_temperature)
        : null,
      Temperature_control_mode: formData.Temperature_control_mode
        ? formData.Temperature_control_mode
        : "Black Panel temperature BPT", // Default to BPT if not set
      DUT_average_temperature: formData.DUT_average_temperature
        ? parseFloat(formData.DUT_average_temperature)
        : null,
    };
    //     console.log("Auto-updated payload:", payload);
    dispatch({ type: "UVID_STRESS_DETAILS", payload });
  }, [formData, dispatch]);

  return (
    <div>
      {state.mode === STRESSOR_CHECK_OUT_MODE && state.isUvidTestCheck && (
        <Paper elevation={3} style={{ padding: 10, marginBottom: 16 }}>
          <Grid
            container
            direction="row"
            justifyContent="space-around"
            alignItems="flex-start"
            spacing={2}
            style={{ marginTop: 16, marginBottom: 16 }}
          >
            <Grid item xs={6}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    name="UVAB_irradiance"
                    label="UVAB irradiance"
                    type="number"
                    value={formData.UVAB_irradiance}
                    onChange={handleChange}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">W/m²</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="UVAB_total_dose"
                    label="UVAB total dose"
                    type="number"
                    value={formData.UVAB_total_dose}
                    onChange={handleChange}
                    fullWidth
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">kWh/m²</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="UVB_irradiance_ratio"
                    label="UVB irradiance ratio"
                    type="number"
                    value={formData.UVB_irradiance_ratio}
                    onChange={handleChange}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    select
                    name="Type_of_light_source"
                    label="Type of light source"
                    value={formData.Type_of_light_source}
                    onChange={handleChange}
                    fullWidth
                  >
                    {lightSourceOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    select
                    name="DUT_operating_condition"
                    label="DUT operating condition"
                    value={
                      formData.DUT_operating_condition
                        ? formData.DUT_operating_condition
                        : UnitVersion === "v12"
                          ? "MPP Maximum Power Point"
                          : "SC Short-circuit"
                    }
                    onChange={handleChange}
                    fullWidth
                    required
                  >
                    {dutOperatingModes.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {UnitVersion && UnitVersion !== "v12" && (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        select
                        name="DUT_exposed_surface"
                        label="DUT exposed surface"
                        value={formData.DUT_exposed_surface}
                        onChange={handleChange}
                        fullWidth
                      >
                        {dutExposedSurfaceOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <TextField
                    name="Set_temperature"
                    label="Set temperature"
                    type="number"
                    value={formData.Set_temperature}
                    onChange={handleChange}
                    fullWidth
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">°C</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    select
                    name="Temperature_control_mode"
                    label="Temperature control mode"
                    value={
                      formData.Temperature_control_mode
                        ? formData.Temperature_control_mode
                        : "Black Panel temperature BPT"
                    }
                    // defaultValue="Black Panel temperature BPT" // BPT as default
                    onChange={handleChange}
                    required
                    fullWidth
                  >
                    {temperatureControlOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <DateTimePicker
                    label="Calibration Date"
                    format="YYYY-MM-DD HH:mm"
                    fullWidth
                    value={formData.Calibration_date}
                    onChange={handleDateChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="DUT_average_temperature"
                    label="DUT average temperature"
                    type="number"
                    value={formData.DUT_average_temperature}
                    onChange={handleChange}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">°C</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      )}
    </div>
  );
};
